// Libraries
import React from 'react';

// Supermove
import {DataLoader, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useState, useQuery, useScrollView, useNavigationDOM, useMemo} from '@supermove/hooks';
import {CommunicationKindModel, ProjectModel, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import ErrorState from '@shared/design/components/EmptyState/ErrorState';
import Line from '@shared/design/components/Line';
import CommunicationWidgetTabs from '@shared/modules/Communication/components/CommunicationWidgetTabs';
import CommunicationsWidgetBody from '@shared/modules/Communication/components/CommunicationsWidgetBody';
import ConversationFooter from '@shared/modules/Communication/components/Footer/ConversationFooter';
import ErrorCatcher from '@shared/modules/Error/components/ErrorCatcher';
import ConversationWidgetSearchAndActions from '@shared/modules/Project/components/widgets/components/ConversationWidgetSearchAndActions';
import WidgetBuilder from '@shared/modules/Project/components/widgets/components/WidgetBuilder';
import useEventListenerForProject from '@shared/modules/SSE/hooks/useEventListenerForProject';

const ContentContainer = Styled.View`
  flex: 1;
  background-color: ${colors.white};
`;

const useCommunicationCounts = ({project}: {project: ProjectModel}) => {
  return useMemo(() => {
    if (!project.communications) {
      return {
        emailCount: 0,
        textMessageCount: 0,
        callCount: 0,
      };
    }
    return project.communications.reduce(
      (acc, communication) => {
        if (communication.email) {
          acc.emailCount += 1;
        }
        if (communication.textMessage) {
          acc.textMessageCount += 1;
        }
        if (communication.call) {
          acc.callCount += 1;
        }
        return acc;
      },
      {emailCount: 0, textMessageCount: 0, callCount: 0},
    );
  }, [project.communications]);
};

const getCommunications = ({
  project,
  communicationKind,
}: {
  project: ProjectModel;
  communicationKind?: CommunicationKindModel;
}) => {
  if (!communicationKind) {
    return project.communications;
  }
  return project.communications.filter((communication) => {
    if (communicationKind === 'EMAIL') {
      return !!communication.email;
    }
    if (communicationKind === 'SMS') {
      return !!communication.textMessage;
    }
    if (communicationKind === 'CALL') {
      return !!communication.call;
    }
    return false;
  });
};

const ProjectConversationsWidgetContent = ({
  project,
  viewer,
  setSearchQuery,
  isLoading,
  refetch,
}: {
  project: ProjectModel;
  viewer: UserModel;
  setSearchQuery: (query: string) => void;
  isLoading: boolean;
  refetch: () => void;
}) => {
  const {params} = useNavigationDOM();
  const conversationsScrollView = useScrollView();
  const [activeActionType, setActiveActionType] = useState<CommunicationKindModel>('SMS');
  const [replyThreadUuid, setReplyThreadUuid] = useState<string>();
  const [footerExpanded, setFooterExpanded] = useState(false);
  const [communicationKind, setCommunicationKind] = useState<CommunicationKindModel>();

  useEventListenerForProject({
    projectId: project.id,
    handleEvent: refetch,
    isDisabled: !project.organization.features.isEnabledRealTimeMessages,
  });

  const communicationCounts = useCommunicationCounts({project});

  return (
    <ContentContainer>
      {footerExpanded ? null : (
        <React.Fragment>
          <Space height={16} />
          <ConversationWidgetSearchAndActions
            project={project}
            setSearchQuery={setSearchQuery}
            isLoading={isLoading}
          />
          <Space height={8} />
          <CommunicationWidgetTabs
            activeTab={communicationKind}
            handleChangeTab={(newActiveTag) => {
              setCommunicationKind(newActiveTag);
              if (newActiveTag) {
                setActiveActionType(newActiveTag);
                if (newActiveTag !== 'EMAIL') {
                  setReplyThreadUuid(undefined);
                }
              }
            }}
            communicationCounts={communicationCounts}
          />
          <Line />
          <CommunicationsWidgetBody
            viewer={viewer}
            conversations={getCommunications({project, communicationKind})}
            contactName={project.client.primaryContact.fullName}
            conversationsScrollView={conversationsScrollView}
            handleReplyThread={(newThreadUuid) => {
              setActiveActionType('EMAIL');
              setReplyThreadUuid(newThreadUuid);
            }}
          />
        </React.Fragment>
      )}
      <ErrorCatcher ErrorComponent={() => <ErrorState title={'Something went wrong.'} />}>
        <ConversationFooter
          customerUuid={project.client.primaryContact.uuid}
          projectUuid={project.uuid}
          refetch={refetch}
          selectedTextMessageTemplateUuid={params.textMessageTemplateUuid}
          activeActionType={activeActionType}
          updateActiveActionType={(newActionType) => {
            setActiveActionType(newActionType);
            if (newActionType !== 'EMAIL') {
              setReplyThreadUuid(undefined);
              if (footerExpanded) {
                setFooterExpanded(false);
              }
            }
          }}
          showSingleActionType={communicationKind}
          replyThreadUuid={replyThreadUuid}
          setReplyThreadUuid={setReplyThreadUuid}
          footerExpanded={footerExpanded}
          setFooterExpanded={setFooterExpanded}
        />
      </ErrorCatcher>
    </ContentContainer>
  );
};

const ProjectConversationsWidget = ({projectUuid}: {projectUuid: string}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const {loading, data, refetch} = useQuery<{
    viewer: UserModel;
    project: ProjectModel;
  }>(ProjectConversationsWidget.query, {
    fetchPolicy: 'network-only',
    variables: {
      projectUuid,
      searchQuery,
    },
  });

  return (
    <DataLoader loading={loading} data={data} as={WidgetBuilder.Loader}>
      {({loadedData}) => {
        return (
          <ProjectConversationsWidgetContent
            project={loadedData.project}
            viewer={loadedData.viewer}
            setSearchQuery={setSearchQuery}
            isLoading={loading}
            refetch={refetch}
          />
        );
      }}
    </DataLoader>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectConversationsWidget.query = gql`
  ${CommunicationsWidgetBody.fragment}
  ${ConversationWidgetSearchAndActions.fragment}

  query ProjectConversationsWidget(
    $projectUuid: String!,
    $searchQuery: String,
  ) {
    ${gql.query}
    viewer {
        id
        ...CommunicationsWidgetBody_Viewer
      }
      project(uuid: $projectUuid) {
        id
        uuid
        organization {
          id
          features {
            isEnabledRealTimeMessages: isEnabled(feature: "REAL_TIME_MESSAGES")
          }
        }
        client {
          id
          uuid
          primaryContact {
            id
            uuid
            fullName
          }
        }
        communications(searchQuery: $searchQuery) {
          id
          email {
            id
          }
          textMessage {
            id
          }
          call {
            id
          }
          ...CommunicationsWidgetBody_Communication
        }
        ...ConversationWidgetSearchAndActions
      }
  }
`;

export default ProjectConversationsWidget;
